<template>
	<div class="temp-control-container">
		<section class="temp-control-section">
			<div class="container">
				<!-- Section Header -->
				<header class="section-header center reveal">
					<h2 class="section-title">{{ $t('refrigeratedGoods.title') }}</h2>
					<p class="section-subtitle">{{ $t('refrigeratedGoods.subtitle') }}</p>
				</header>

				<!-- Solution Introduction -->
				<div class="solution-intro">
					<p class="intro-text">{{ $t('refrigeratedGoods.introduction') }}</p>
				</div>

				<!-- Features Grid -->
				<div class="features-grid">
					<div v-for="(feature, index) in $t('refrigeratedGoods.features')" :key="`feature-${index}`"
						class="feature-card">
						<div class="feature-icon" :style="{ backgroundColor: feature.color }">
							<i :class="feature.icon"></i>
						</div>
						<h3 class="feature-title">{{ feature.title }}</h3>
						<p class="feature-desc">{{ feature.description }}</p>
					</div>
				</div>

				<!-- Active Control Section -->
				<section class="active-control-section">
					<header class="subsection-header">
						<h3 class="subsection-title">{{ $t('refrigeratedGoods.activeControl.title') }}</h3>
						<p class="subsection-subtitle">{{ $t('refrigeratedGoods.activeControl.subtitle') }}</p>
					</header>

					<div class="equipment-showcase">
						<div v-for="(equip, index) in $t('refrigeratedGoods.activeControl.equipment')"
							:key="`equip-${index}`" class="equipment-card">
							<div class="equip-image-container">
								<div class="image-wrapper">
									<img v-lazy-img="getImage(equip.image)" :alt="equip.name" class="equip-image"
										@error="handleImageError">
								</div>
							</div>
							<div class="equip-info">
								<h4 class="equip-name">{{ equip.name }}</h4>
								<p class="equip-desc">{{ equip.description }}</p>
								<div class="equip-specs">
									<span v-for="(spec, i) in equip.specs" :key="`spec-${i}`" class="spec-item">
										<i :class="['spec-icon', spec.icon]"></i> {{ spec.text }}
									</span>
								</div>
								<button v-if="equip.cta" class="equip-cta">
									{{ equip.cta }}
								</button>
							</div>
						</div>
					</div>
				</section>
				<!-- Passive Control Section -->
				<section class="passive-control-section">
					<header class="subsection-header">
						<h3 class="subsection-title">{{ $t('refrigeratedGoods.passiveControl.title') }}</h3>
						<p class="subsection-subtitle">{{ $t('refrigeratedGoods.passiveControl.subtitle') }}</p>
					</header>
					<!-- 被动温控设备展示 -->
					<div v-if="$t('refrigeratedGoods.passiveControl.equipment') && $t('refrigeratedGoods.passiveControl.equipment').length"
						class="equipment-showcase">
						<div v-for="(equip, index) in $t('refrigeratedGoods.passiveControl.equipment')"
							:key="`p-equip-${index}`" class="equipment-card">
							<div class="equip-image-container">
								<div class="image-wrapper">
									<img v-lazy-img="getImage(equip.image)" :alt="equip.name" class="equip-image"
										@error="handleImageError">
								</div>
							</div>
							<div class="equip-info">
								<h4 class="equip-name">{{ equip.name }}</h4>
								<p class="equip-desc">{{ equip.description }}</p>
								<div class="equip-specs">
									<span v-for="(spec, i) in equip.specs" :key="`p-spec-${i}`" class="spec-item">
										<i :class="['spec-icon', spec.icon]"></i> {{ spec.text }}
									</span>
								</div>
								<button v-if="equip.cta" class="equip-cta">
									{{ equip.cta }}
								</button>
							</div>
						</div>
					</div>
					<div class="passive-options-grid">
						<div v-for="(option, index) in $t('refrigeratedGoods.passiveControl.options')"
							:key="`option-${index}`" class="option-card">
							<div class="option-header">
								<div class="option-icon" :style="{ color: option.color }">
									<img v-lazy-img="getImage(option.icon)" class="icon-image" />
								</div>
								<h4 class="option-title">{{ option.title }}</h4>
							</div>
							<p class="option-desc">{{ option.description }}</p>
							<ul class="option-features">
								<li v-for="(feature, i) in option.features" :key="`feature-${i}`">
									{{ feature }}
								</li>
							</ul>
						</div>
					</div>
				</section>
			</div>
		</section>
	</div>
</template>

<script>
	export default {
		name: 'RefrigeratedGoods',
		methods: {
			getImage(filename) {
				try {
					return require(`@/assets/images/${filename}`);
				} catch (e) {
					console.warn(`图片加载失败: ${filename}`);
					return '';
				}
			},
			handleImageError(event) {
				event.target.style.opacity = '0.2';
				event.target.alt = '图片加载失败';
			}
		}
	};
</script>

<style lang="scss" scoped>
	// === Design System Variables (aligned with Home.vue) ===
	$primary: #0052CC;
	$primary-dark: #0043A8;
	$primary-light: #3B7FFF;
	$silver: #8E9EAB;
	$light: #F4F7FB;
	$body: #5B6C7F;
	$heading: #0A1628;
	$success: #10b981;

	// === Responsive Breakpoints ===
	$bp-desktop: 1200px;
	$bp-tablet-lg: 992px;
	$bp-tablet: 768px;
	$bp-mobile-lg: 576px;
	$bp-mobile: 480px;

	/* ========================================
	   基础样式
	   ======================================== */
	.temp-control-container {
		font-family: 'Rubik', 'Poppins', system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
		color: $body;
		line-height: 1.6;
		background: #fff;
	}

	.temp-control-section {
		padding: clamp(40px, 5vw, 80px) 0;
	}

	.container {
		max-width: 1280px;
		margin: 0 auto;
		padding: 0 clamp(16px, 3vw, 40px);
	}

	/* ========================================
	   Section Header
	   ======================================== */
	.section-header {
		text-align: center;
		margin-bottom: clamp(28px, 4vw, 50px);

		.section-title {
			font-size: clamp(1.5rem, 3.5vw, 2.6rem);
			font-weight: 700;
			margin: 0 0 12px 0;
			color: $heading;
			letter-spacing: -0.5px;
			line-height: 1.2;

			&::after {
				content: '';
				display: block;
				width: clamp(50px, 6vw, 80px);
				height: 4px;
				background: linear-gradient(to right, $primary, $primary-light);
				border-radius: 2px;
				margin: 12px auto 0;
			}
		}

		.section-subtitle {
			font-size: clamp(0.9rem, 1.2vw, 1.05rem);
			color: #666;
			max-width: 760px;
			margin: clamp(16px, 2vw, 22px) auto 0;
			font-weight: 400;
			line-height: 1.7;
			padding: 0 8px;
		}
	}

	/* ========================================
	   Solution Introduction
	   ======================================== */
	.solution-intro {
		max-width: 800px;
		margin: 0 auto clamp(30px, 4vw, 50px);
		text-align: center;
		padding: 0 4px;
	}

	.intro-text {
		font-size: clamp(0.92rem, 1.3vw, 1.1rem);
		line-height: 1.7;
		color: $body;
	}

	/* ========================================
	   Features Grid
	   ======================================== */
	.features-grid {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: clamp(16px, 2vw, 24px);
		margin-bottom: clamp(40px, 5vw, 60px);

		@media (max-width: $bp-tablet-lg) {
			grid-template-columns: repeat(2, 1fr);
		}

		@media (max-width: $bp-mobile) {
			grid-template-columns: 1fr;
		}
	}

	.feature-card {
		background: #fff;
		border-radius: 10px;
		padding: clamp(18px, 2.5vw, 28px) clamp(14px, 2vw, 22px);
		text-align: center;
		box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
		transition: all 0.35s cubic-bezier(0.22, 1, 0.36, 1);

		&:hover {
			transform: translateY(-5px);
			box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
		}

		@media (max-width: $bp-mobile) {
			&:hover {
				transform: translateY(-3px);
			}
		}
	}

	.feature-icon {
		width: clamp(56px, 6vw, 70px);
		height: clamp(56px, 6vw, 70px);
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		margin: 0 auto clamp(12px, 1.5vw, 18px);
		color: #fff;
		font-size: clamp(1.3rem, 2vw, 1.8rem);
	}

	.feature-title {
		font-size: clamp(1rem, 1.3vw, 1.25rem);
		color: $heading;
		margin-bottom: 8px;
		font-weight: 700;
	}

	.feature-desc {
		font-size: clamp(0.85rem, 1vw, 0.95rem);
		color: $body;
		line-height: 1.6;
	}

	/* ========================================
	   Subsection Shared
	   ======================================== */
	.subsection-header {
		text-align: center;
		margin-bottom: clamp(24px, 3.5vw, 40px);
	}

	.subsection-title {
		font-size: clamp(1.3rem, 2.5vw, 1.8rem);
		color: $heading;
		margin: 0 0 8px 0;
		font-weight: 700;
		text-align: center;
		position: relative;
		display: inline-block;

		&::after {
			content: '';
			display: block;
			width: 50px;
			height: 3px;
			background: linear-gradient(to right, $primary, $primary-light);
			margin: 12px auto 0;
			border-radius: 2px;
		}
	}

	.subsection-subtitle {
		font-size: clamp(0.88rem, 1.2vw, 1.05rem);
		color: $body;
		margin-top: 12px;
		line-height: 1.6;
		max-width: 720px;
		margin-left: auto;
		margin-right: auto;
		text-align: center;
		padding: 0 8px;
	}

	/* ========================================
	   Active Control — Equipment Showcase
	   ======================================== */
	.active-control-section {
		margin-bottom: clamp(36px, 5vw, 60px);
	}

	.equipment-showcase {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: clamp(16px, 2vw, 24px);

		@media (max-width: $bp-mobile) {
			grid-template-columns: 1fr;
			max-width: 420px;
			margin: 0 auto;
		}
	}

	.equipment-card {
		background: #fff;
		border-radius: 12px;
		overflow: hidden;
		box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
		transition: all 0.35s cubic-bezier(0.22, 1, 0.36, 1);
		display: flex;
		flex-direction: column;
		height: 100%;

		&:hover {
			transform: translateY(-5px);
			box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);

			.equip-image {
				transform: scale(1.04);
			}
		}

		@media (max-width: $bp-mobile) {
			&:hover {
				transform: translateY(-3px);
			}
		}
	}

	.equip-image-container {
		position: relative;
		overflow: hidden;
		height: clamp(160px, 18vw, 200px);
		background: $light;
	}

	.image-wrapper {
		width: 100%;
		height: 100%;
	}

	.equip-image {
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform 0.5s ease;
	}

	.equip-info {
		padding: clamp(12px, 1.8vw, 18px) clamp(14px, 2vw, 20px);
		flex-grow: 1;
		display: flex;
		flex-direction: column;
	}

	.equip-name {
		font-size: clamp(0.98rem, 1.3vw, 1.2rem);
		color: $heading;
		margin: 0 0 8px 0;
		font-weight: 700;
		line-height: 1.3;
	}

	.equip-desc {
		font-size: clamp(0.82rem, 1vw, 0.92rem);
		color: $body;
		margin-bottom: 12px;
		line-height: 1.6;
		flex-grow: 1;
	}

	.equip-specs {
		display: flex;
		flex-wrap: wrap;
		gap: 6px 8px;
		margin-top: 8px;
		margin-bottom: 12px;
	}

	.spec-item {
		font-size: clamp(0.72rem, 0.85vw, 0.8rem);
		background: $light;
		padding: 4px 10px;
		border-radius: 20px;
		color: $body;
		display: inline-flex;
		align-items: center;
		white-space: nowrap;

		.spec-icon {
			margin-right: 5px;
			color: $primary;
			font-size: 0.85rem;
			flex-shrink: 0;
		}
	}

	.equip-cta {
		margin-top: auto;
		background: $primary;
		color: #fff;
		border: none;
		padding: 9px 18px;
		border-radius: 6px;
		cursor: pointer;
		font-size: clamp(0.82rem, 0.9vw, 0.9rem);
		font-weight: 600;
		transition: all 0.3s ease;
		align-self: flex-start;

		&:hover {
			background: $primary-dark;
			transform: translateY(-2px);
			box-shadow: 0 6px 16px rgba(0, 82, 204, 0.3);
		}
	}

	/* ========================================
	   Passive Control Section
	   ======================================== */
	.passive-control-section {
		margin-bottom: clamp(30px, 4vw, 50px);

		.equipment-showcase {
			margin-top: clamp(20px, 3vw, 30px);
		}
	}

	.passive-options-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: clamp(16px, 2vw, 24px);
		margin: clamp(20px, 3vw, 30px) 0;

		@media (max-width: $bp-tablet-lg) {
			grid-template-columns: repeat(2, 1fr);
		}

		@media (max-width: $bp-mobile) {
			grid-template-columns: 1fr;
			max-width: 420px;
			margin-left: auto;
			margin-right: auto;
		}
	}

	.option-card {
		background: #fff;
		border-radius: 10px;
		padding: clamp(16px, 2vw, 24px);
		box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
		transition: all 0.35s cubic-bezier(0.22, 1, 0.36, 1);
		display: flex;
		flex-direction: column;

		&:hover {
			transform: translateY(-5px);
			box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
		}

		@media (max-width: $bp-mobile) {
			&:hover {
				transform: translateY(-3px);
			}
		}
	}

	.option-header {
		display: flex;
		align-items: center;
		margin-bottom: 12px;
		gap: 12px;
	}

	.option-icon {
		width: clamp(40px, 5vw, 48px);
		height: clamp(40px, 5vw, 48px);
		border-radius: 50%;
		background: rgba(0, 82, 204, 0.1);
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	.icon-image {
		width: clamp(22px, 2.5vw, 26px);
		height: clamp(22px, 2.5vw, 26px);
		object-fit: contain;
	}

	.option-title {
		font-size: clamp(0.98rem, 1.3vw, 1.2rem);
		color: $heading;
		font-weight: 700;
	}

	.option-desc {
		font-size: clamp(0.82rem, 1vw, 0.92rem);
		color: $body;
		margin-bottom: 12px;
		line-height: 1.6;
	}

	.option-features {
		list-style-type: none;
		padding-left: 0;
		margin: 0;
		flex-grow: 1;

		li {
			position: relative;
			padding-left: 20px;
			margin-bottom: 6px;
			font-size: clamp(0.8rem, 0.95vw, 0.88rem);
			color: $body;
			line-height: 1.5;

			&::before {
				content: '✓';
				position: absolute;
				left: 0;
				color: $success;
				font-weight: bold;
			}
		}
	}

	/* ========================================
	   Tablet Fine-tuning (768px - 991px)
	   ======================================== */
	@media (min-width: $bp-tablet) and (max-width: ($bp-tablet-lg - 1px)) {
		.equipment-showcase {
			max-width: 720px;
			margin: 0 auto;
		}

		.features-grid {
			max-width: 640px;
			margin-left: auto;
			margin-right: auto;
		}

		.passive-options-grid {
			max-width: 640px;
			margin-left: auto;
			margin-right: auto;
		}
	}

	/* ========================================
	   Mobile Fine-tuning (< 480px)
	   ======================================== */
	@media (max-width: ($bp-mobile - 1px)) {
		.temp-control-section {
			padding: 30px 0;
		}

		.section-header {
			margin-bottom: 24px;
		}

		.features-grid {
			gap: 14px;
		}

		.feature-card {
			padding: 16px 14px;
		}

		.feature-icon {
			width: 52px;
			height: 52px;
			font-size: 1.2rem;
			margin-bottom: 10px;
		}

		.equipment-showcase {
			gap: 14px;
		}

		.equip-image-container {
			height: 180px;
		}

		.equip-info {
			padding: 12px;
		}

		.spec-item {
			font-size: 0.7rem;
			padding: 3px 8px;
		}

		.passive-options-grid {
			gap: 14px;
		}

		.option-card {
			padding: 14px;
		}

		.option-icon {
			width: 38px;
			height: 38px;
		}

		.icon-image {
			width: 20px;
			height: 20px;
		}

		.option-features li {
			font-size: 0.78rem;
			margin-bottom: 4px;
		}
	}
</style>